<script setup lang="ts">
import { ref } from "vue";
import { useEquipment } from "@/composables/tasks/useEquipment";
import { useEquipmentForm } from "@/composables/tasks/useEquipmentForm";
import EquipmentAddForm from "./Forms/EquipmentAddForm.vue";

// ===================================================================
// State
// ===================================================================
const showAddForm = ref(false);
const selectedTemplateId = ref<string | number | null>(null);

// ===================================================================
// Composables
// ===================================================================
// Equipment operations
const { getAvailableEquipmentTypes } = useEquipment();

// Equipment form
const {
  initAddForm,
  cancelForm,
} = useEquipmentForm();

// ===================================================================
// Actions
// ===================================================================
// Start adding a new configuration
const startAddingConfig = (typeId: string | number) => {
  selectedTemplateId.value = typeId;
  initAddForm(typeId);
  showAddForm.value = true;
};

// Handle form cancellation
const handleCancel = () => {
  cancelForm();
  showAddForm.value = false;
  selectedTemplateId.value = null;
};

// Handle successful save
const handleSaved = () => {
  showAddForm.value = false;
  selectedTemplateId.value = null;
};
</script>

<template>
  <div>
    <!-- Add Configuration Form -->
    <EquipmentAddForm
        v-if="showAddForm"
        @cancel="handleCancel"
        @saved="handleSaved"
    />

    <!-- Display Available Equipment Types -->
    <div v-else>
      <div v-if="getAvailableEquipmentTypes && getAvailableEquipmentTypes.length > 0" class="space-y-4">
        <h2 class="text-lg font-semibold">Available Equipment Types</h2>

        <div v-for="type in getAvailableEquipmentTypes" :key="type.id" class="border rounded p-4 bg-white">
          <div class="flex justify-between items-center">
            <div>
              <h3 class="text-md font-semibold">{{ type.name }}</h3>
              <p v-if="type.description" class="text-sm text-gray-600 mt-1">
                {{ type.description }}
              </p>
            </div>

            <button
                @click="startAddingConfig(type.id)"
                class="bg-primary text-white px-3 py-1 rounded text-sm">
                Add Configuration
            </button>
          </div>
        </div>
      </div>
      <div v-else class="p-4 text-center bg-gray-50 border rounded">
        <p class="text-gray-500">All available equipment types have been configured.</p>
      </div>
    </div>
  </div>
</template>